import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import CampaignFormModal from '../../components/admin/CampaignFormModal';
import { apiRequest, getImageUrl } from '../../utils/api';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Drag Handle Icon
const DragHandleIcon = ({ className = "w-5 h-5" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
    </svg>
);

// Arrow Up Icon
const ArrowUpIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
);

// Arrow Down Icon
const ArrowDownIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

// Sortable Campaign Row Component (List View)
const SortableCampaignRow = ({ 
    campaign, 
    index,
    total,
    onEdit,
    onDelete,
    onMoveUp,
    onMoveDown,
    onSortOrderChange,
    savingOrder,
    getCampaignTypeLabel
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: campaign.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    const [editingSortOrder, setEditingSortOrder] = useState(false);
    const [sortOrderValue, setSortOrderValue] = useState(campaign.sort_order || 0);

    const handleSortOrderSubmit = () => {
        const newOrder = parseInt(sortOrderValue, 10);
        if (!isNaN(newOrder) && newOrder !== campaign.sort_order) {
            onSortOrderChange(campaign, newOrder);
        }
        setEditingSortOrder(false);
    };

    // Format discount value for display
    const getDiscountDisplay = () => {
        if (campaign.discount_type === 'percentage') {
            // Remove decimal places for percentage (e.g., 10.00 -> 10)
            const value = parseFloat(campaign.discount_value);
            return `%${Math.round(value)}`;
        } else if (campaign.discount_value) {
            return `${parseFloat(campaign.discount_value).toFixed(0)}₺`;
        }
        return '-';
    };

    return (
        <tr 
            ref={setNodeRef} 
            style={style} 
            className={`bg-white border-b hover:bg-gray-50 transition ${isDragging ? 'shadow-lg' : ''}`}
        >
            {/* Drag Handle & Sort Controls */}
            <td className="px-4 py-3 w-20">
                <div className="flex items-center gap-1">
                    <button
                        {...attributes}
                        {...listeners}
                        className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                        title="Sürükle"
                    >
                        <DragHandleIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => onMoveUp(campaign, index)}
                        disabled={index === 0 || savingOrder}
                        className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Yukarı"
                    >
                        <ArrowUpIcon className="w-3 h-3" />
                    </button>
                    <button
                        onClick={() => onMoveDown(campaign, index)}
                        disabled={index === total - 1 || savingOrder}
                        className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Aşağı"
                    >
                        <ArrowDownIcon className="w-3 h-3" />
                    </button>
                    {editingSortOrder ? (
                        <input
                            type="number"
                            value={sortOrderValue}
                            onChange={(e) => setSortOrderValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSortOrderSubmit();
                                if (e.key === 'Escape') setEditingSortOrder(false);
                            }}
                            onBlur={handleSortOrderSubmit}
                            className="w-12 px-1 py-0.5 border rounded text-xs"
                            autoFocus
                        />
                    ) : (
                        <button
                            onClick={() => setEditingSortOrder(true)}
                            disabled={savingOrder}
                            className="px-1.5 py-0.5 rounded text-xs font-medium text-gray-600 hover:bg-green-50 hover:text-green-600 disabled:opacity-30"
                            title="Sıra No"
                        >
                            {campaign.sort_order || 0}
                        </button>
                    )}
                </div>
            </td>
            
            {/* Image */}
            <td className="px-4 py-3 w-20">
                {(campaign.media_url || campaign.campaign_image) ? (
                    <img
                        src={getImageUrl(campaign.campaign_image || campaign.media_url)}
                        alt={campaign.title}
                        className="w-16 h-16 object-cover rounded"
                    />
                ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                )}
            </td>
            
            {/* Title */}
            <td className="px-4 py-3">
                <div className="font-semibold text-gray-900">{campaign.title}</div>
                {campaign.description && (
                    <div className="text-sm text-gray-500 mt-1 line-clamp-1">{campaign.description}</div>
                )}
            </td>
            
            {/* Type */}
            <td className="px-4 py-3">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                    {getCampaignTypeLabel(campaign.campaign_type)}
                </span>
            </td>
            
            {/* Discount */}
            <td className="px-4 py-3">
                <span className="text-sm font-medium text-gray-900">
                    {getDiscountDisplay()}
                </span>
            </td>
            
            {/* Status */}
            <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                    campaign.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                    {campaign.status === 'active' ? 'Aktif' : 'Pasif'}
                </span>
            </td>
            
            {/* Date Range */}
            <td className="px-4 py-3 text-sm text-gray-600">
                {campaign.start_date && campaign.end_date ? (
                    <div>
                        <div>{new Date(campaign.start_date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}</div>
                        <div className="text-xs text-gray-400">-</div>
                        <div>{new Date(campaign.end_date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}</div>
                    </div>
                ) : (
                    <span className="text-green-600 font-medium text-xs">Süresiz</span>
                )}
            </td>
            
            {/* Actions */}
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onEdit(campaign)}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                    >
                        Düzenle
                    </button>
                    <button
                        onClick={() => onDelete(campaign.id)}
                        className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                    >
                        Sil
                    </button>
                </div>
            </td>
        </tr>
    );
};

const CampaignsPage = () => {
    const { organizationId } = useParams();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [savingOrder, setSavingOrder] = useState(false);
    
    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
        useSensor(TouchSensor)
    );

    useEffect(() => {
        loadCampaigns();
    }, [organizationId]);

    const loadCampaigns = async () => {
        setLoading(true);
        try {
            const response = await apiRequest(`/organizations/${organizationId}/campaigns`);
            const sorted = (response.data || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            setCampaigns(sorted);
        } catch (error) {
            console.error('Load campaigns error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Bu kampanyayı silmek istediğinize emin misiniz?')) {
            return;
        }

        try {
            await apiRequest(`/organizations/${organizationId}/campaigns/${id}`, {
                method: 'DELETE'
            });
            loadCampaigns();
        } catch (error) {
            alert('Kampanya silinirken bir hata oluştu');
        }
    };

    const getCampaignTypeLabel = (type) => {
        const labels = {
            discount: 'İndirim',
            combo: 'Kombinasyon',
            buy_x_get_y: 'Al X Get Y',
            quantity_discount: 'Adet Bazlı İndirim',
            combination: 'Kombinasyon Kampanyası',
            cross_product: 'X Alana Y Üründe İndirim',
            cross_category: 'X Alana Y Kategorisinde İndirim'
        };
        return labels[type] || type;
    };

    // Handle campaign reorder (drag and drop)
    const handleCampaignDragEnd = async (campaigns, oldIndex, newIndex) => {
        const reorderedCampaigns = arrayMove(campaigns, oldIndex, newIndex);
        
        const updates = reorderedCampaigns.map((campaign, index) => ({
            ...campaign,
            sort_order: index + 1
        }));

        setCampaigns(updates);

        setSavingOrder(true);
        try {
            const payload = { 
                campaigns: updates.map(c => ({ 
                    id: parseInt(c.id, 10), 
                    sort_order: parseInt(c.sort_order, 10) 
                })) 
            };
            console.log('🔄 Saving campaign order:', JSON.stringify(payload, null, 2));
            await apiRequest(`/organizations/${organizationId}/campaigns/reorder`, {
                method: 'PUT',
                data: payload
            });
            // Reload campaigns to ensure sync with backend
            await loadCampaigns();
        } catch (error) {
            console.error('❌ Failed to save campaign order:', error);
            console.error('Error details:', error.response?.data || error.message);
            // Reload on error to restore correct state
            loadCampaigns();
        } finally {
            setSavingOrder(false);
        }
    };

    // Handle move up
    const handleMoveUp = (campaign, index) => {
        if (index === 0) return;
        handleCampaignDragEnd(campaigns, index, index - 1);
    };

    // Handle move down
    const handleMoveDown = (campaign, index) => {
        if (index === campaigns.length - 1) return;
        handleCampaignDragEnd(campaigns, index, index + 1);
    };

    // Handle sort order change
    const handleSortOrderChange = async (campaign, newOrder) => {
        const updates = [{ id: campaign.id, sort_order: newOrder }];
        
        setCampaigns(prev => prev.map(c => 
            c.id === campaign.id ? { ...c, sort_order: newOrder } : c
        ));

        setSavingOrder(true);
        try {
            const payload = { 
                campaigns: updates.map(u => ({ 
                    id: parseInt(u.id, 10), 
                    sort_order: parseInt(u.sort_order, 10) 
                })) 
            };
            console.log('🔄 Saving campaign sort order:', payload);
            await apiRequest(`/organizations/${organizationId}/campaigns/reorder`, {
                method: 'PUT',
                data: payload
            });
            // Reload campaigns to ensure sync with backend
            await loadCampaigns();
        } catch (error) {
            console.error('❌ Failed to save campaign sort order:', error);
            console.error('Error details:', error.response?.data || error.message);
            // Reload on error to restore correct state
            loadCampaigns();
        } finally {
            setSavingOrder(false);
        }
    };

    return (
        <AdminLayout>
            <div>
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Kampanyalar</h1>
                    <button
                        onClick={() => {
                            setEditingCampaign(null);
                            setShowModal(true);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                        + Yeni Kampanya
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : campaigns.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                        <p className="text-gray-500 mb-4">Henüz kampanya eklenmemiş.</p>
                        <button
                            onClick={() => setShowModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            İlk Kampanyayı Oluştur
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => {
                                const { active, over } = event;
                                if (!over || active.id === over.id) return;

                                const oldIndex = campaigns.findIndex(c => c.id === active.id);
                                const newIndex = campaigns.findIndex(c => c.id === over.id);

                                if (oldIndex !== -1 && newIndex !== -1) {
                                    handleCampaignDragEnd(campaigns, oldIndex, newIndex);
                                }
                            }}
                        >
                            <SortableContext items={campaigns.map(c => c.id)} strategy={rectSortingStrategy}>
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                                Sıra
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                                Görsel
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Başlık
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Tip
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                İndirim
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Durum
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Tarih Aralığı
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                İşlemler
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {campaigns.map((campaign, index) => (
                                            <SortableCampaignRow
                                                key={campaign.id}
                                                campaign={campaign}
                                                index={index}
                                                total={campaigns.length}
                                                onEdit={async (c) => {
                                                    try {
                                                        // Fetch full campaign details with products
                                                        const response = await apiRequest(`/organizations/${organizationId}/campaigns/${c.id}`);
                                                        setEditingCampaign(response.data);
                                                        setShowModal(true);
                                                    } catch (error) {
                                                        console.error('Load campaign details error:', error);
                                                        // Fallback to basic campaign data
                                                        setEditingCampaign(c);
                                                        setShowModal(true);
                                                    }
                                                }}
                                                onDelete={handleDelete}
                                                onMoveUp={handleMoveUp}
                                                onMoveDown={handleMoveDown}
                                                onSortOrderChange={handleSortOrderChange}
                                                savingOrder={savingOrder}
                                                getCampaignTypeLabel={getCampaignTypeLabel}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </SortableContext>
                        </DndContext>
                    </div>
                )}

                {/* Campaign Form Modal */}
                <CampaignFormModal
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        setEditingCampaign(null);
                    }}
                    campaign={editingCampaign}
                    organizationId={organizationId}
                    onSuccess={loadCampaigns}
                />
            </div>
        </AdminLayout>
    );
};

export default CampaignsPage;

